import {
  nursesMonthlyEntitlement,
  nursesReviewReasonText,
  type NursesMonthlyEvidence,
} from './leave-nurses-monthly-entitlement';
import {
  addDays,
  dayOfWeek,
  gsQualifiesByRosterPattern,
  gsQualifyingWeekendDates,
  type LeaveAgreement,
  type OrdinaryShiftFact,
} from './leave-entitlement-policy';
import { shiftCalendarMonths } from './nurses-personal-leave-basis';
import { calculateCorrectionResidual, type LeaveCorrectionScope } from './leave-correction';
import {
  correctionScopesOverlap,
  type CorrectionCandidate,
  type CorrectionCredit,
} from './leave-correction-proposals';

export const SHIFTWORKER_RULE_VERSION = '2026-10-02.shiftworker.4';

export interface ShiftworkerHistory {
  staffId: string;
  employeeId: string;
  leaveTypeId: string | null;
  calendarId: string;
  startDate: string;
  asOf: string;
  closedThrough: string | null;
  agreement: LeaveAgreement;
  employment: 'FULL_TIME' | 'PART_TIME' | 'CASUAL';
  /** Approved worked ordinary shifts only; includes pre-cutover service-year/lookback context. */
  shifts: readonly OrdinaryShiftFact[];
  nursesEvidence?: NursesMonthlyEvidence;
  /** Earliest calendar date covered by the bounded reader, not first attendance. */
  historyFrom?: string;
  complete: boolean;
  /** Every approved row has a verified ordinary/overtime split; never infer from total hours. */
  ordinarySplitComplete: boolean;
  /** Digest of approved facts supplied by the server; raw shifts are never retained in evidence. */
  shiftDigest: string;
  /** Certified complete closed position per service year, including imported loads. Absence is unknown. */
  closedAdditionalCredits?: readonly {
    serviceYearStart: string;
    through: string;
    units1e4: number;
    evidenceRefs: string[];
  }[];
  /** Actual provider additional units allocated by PR2 period evidence, not current template assumptions. */
  providerPeriods: readonly {
    start: string;
    end: string;
    additionalUnits1e4: number | null;
    headId: string;
    payslipIds: string[];
    payRunIds: string[];
    allocationDates?: Array<{ date: string; units1e4: number }> | null;
  }[];
  ledger: readonly CorrectionCredit[];
}

export function shiftworkerServiceYear(startDate: string, date: string) {
  let start = shiftCalendarMonths(
    startDate,
    (Number(date.slice(0, 4)) - Number(startDate.slice(0, 4))) * 12,
  );
  if (start > date)
    start = shiftCalendarMonths(
      startDate,
      (Number(date.slice(0, 4)) - Number(startDate.slice(0, 4)) - 1) * 12,
    );
  // Always derive anniversaries from engagement date (29 February clamps only in non-leap years).
  const endExclusive = shiftCalendarMonths(
    startDate,
    (Number(start.slice(0, 4)) - Number(startDate.slice(0, 4)) + 1) * 12,
  );
  return { start, endExclusive };
}

/** Evaluate both GS and/or routes; compare cumulative entitlements on every worked date. */
export function qualifyShiftworkerYear(
  input: ShiftworkerHistory,
  start: string,
  endExclusive: string,
) {
  const end = [input.asOf, addDays(endExclusive, -1)].sort()[0]!;
  const eligible = input.shifts.filter((s) => s.date >= start && s.date <= end);
  const progressiveDates = [...new Set(eligible.map((s) => s.date))]
    .sort()
    .filter((date) =>
      input.agreement === 'GENERAL_STAFF'
        ? gsQualifiesByRosterPattern(input.shifts, date).qualifies
        : false,
    );
  // Read the whole calendar weekend, including the adjacent service year. Ownership is Saturday's year.
  const weekendContext = input.shifts.filter((s) => s.date <= input.asOf);
  const inclusive = gsQualifyingWeekendDates(weekendContext, start, endExclusive, 'AT_LEAST_FOUR');
  const strict = gsQualifyingWeekendDates(weekendContext, start, endExclusive, 'MORE_THAN_FOUR');
  const gsDue = (through: string, route: 'PROGRESSIVE' | 'WEEKEND', dates: string[]) => {
    if (route === 'WEEKEND' && dates.filter((d) => d <= through).length < 10) return 0;
    const hours = eligible
      .filter(
        (s) => s.date <= through && (route === 'WEEKEND' || progressiveDates.includes(s.date)),
      )
      .reduce((sum, s) => sum + s.ordinaryMinutes / 60, 0);
    return Math.round(Math.min(38, (hours * 38) / 1976) * 10000);
  };
  const comparisonDates = [
    ...new Set([...eligible.map((s) => s.date), ...inclusive, ...strict]),
  ].sort();
  const progressive = gsDue(input.asOf, 'PROGRESSIVE', inclusive);
  const inclusiveWeekend = gsDue(input.asOf, 'WEEKEND', inclusive);
  const weekend = gsDue(input.asOf, 'WEEKEND', strict);
  const strictWeekend = gsDue(input.asOf, 'WEEKEND', strict);
  const dominates = (a: 'PROGRESSIVE' | 'WEEKEND', b: 'PROGRESSIVE' | 'WEEKEND', dates: string[]) =>
    comparisonDates.every((d) => gsDue(d, a, dates) >= gsDue(d, b, dates));
  const routeConflict =
    !dominates('PROGRESSIVE', 'WEEKEND', strict) && !dominates('WEEKEND', 'PROGRESSIVE', strict);
  const thresholdConflict = comparisonDates.some(
    (d) =>
      Math.max(gsDue(d, 'PROGRESSIVE', inclusive), gsDue(d, 'WEEKEND', inclusive)) !==
      Math.max(gsDue(d, 'PROGRESSIVE', strict), gsDue(d, 'WEEKEND', strict)),
  );
  const provisoDate = shiftCalendarMonths(input.startDate, 12);
  const nursesEntitlement =
    input.agreement === 'NURSES'
      ? nursesMonthlyEntitlement(
          input.nursesEvidence ?? {
            segments: [],
            roster: [],
            coverage: [],
            accrualDays: [],
            engagementComplete: false,
            absenceComplete: false,
            reviewReasons: ['NURSES_ROSTER_EVIDENCE_MISSING'],
          },
          input.startDate,
          input.asOf,
          start,
          endExclusive,
        )
      : null;
  const nursesMonths = nursesEntitlement?.months ?? [];
  const closedEnd = input.closedThrough
    ? [input.closedThrough, addDays(endExclusive, -1)].sort()[0]!
    : null;
  const closedScope: LeaveCorrectionScope = {
    type: 'PERIOD',
    calendarId: input.calendarId,
    start,
    end: closedEnd ?? start,
  };
  const closedLedger = input.ledger.filter(
    (c) =>
      c.xero_employee_id === input.employeeId &&
      c.xero_leave_type_id === input.leaveTypeId &&
      c.component === 'ADDITIONAL_SHIFTWORKER' &&
      closedEnd &&
      closedEnd >= start &&
      correctionScopesOverlap(c.scope, closedScope),
  );
  // An exact attributed closed-position import establishes an actual amount. Partial/straddling history cannot.
  const ledgerKnown =
    closedLedger.length > 0 &&
    new Set(closedLedger.map((c) => c.id)).size === closedLedger.length &&
    closedLedger.every(
      (c) =>
        c.attribution === 'ATTRIBUTED' &&
        c.scope.type === 'PERIOD' &&
        c.scope.start === start &&
        c.scope.end === closedEnd &&
        c.units_1e4 !== null &&
        Number.isSafeInteger(Number(c.units_1e4)),
    );
  const records =
    input.closedAdditionalCredits?.filter(
      (c) => c.serviceYearStart === start && c.through === closedEnd,
    ) ??
    (ledgerKnown
      ? [
          {
            serviceYearStart: start,
            through: closedEnd!,
            units1e4: closedLedger.reduce((sum, c) => sum + Number(c.units_1e4), 0),
            evidenceRefs: closedLedger.map((c) => c.id),
          },
        ]
      : []);
  const closed =
    records.length === 1 &&
    Number.isSafeInteger(records[0]!.units1e4) &&
    records[0]!.units1e4 >= 0 &&
    records[0]!.evidenceRefs.length > 0 &&
    records[0]!.evidenceRefs.every((r) => r.trim())
      ? records[0]!
      : null;
  const baselineKnown =
    !closedEnd ||
    closedEnd < start ||
    (closed !== null &&
      (!closedLedger.length ||
        (ledgerKnown &&
          closed.units1e4 === closedLedger.reduce((sum, c) => sum + Number(c.units_1e4), 0) &&
          closedLedger.every((c) => closed.evidenceRefs.includes(c.id)))));
  const baseline = closed?.units1e4 ?? 0;
  const nurses = input.agreement === 'NURSES';
  const total = nurses ? nursesEntitlement!.expectedUnits1e4 : Math.max(progressive, weekend);
  const strictTotal = nurses ? total : Math.max(progressive, strictWeekend);
  return {
    route: nurses ? 'NURSES_MONTHLY' : weekend > progressive ? 'GS_WEEKEND' : 'GS_PROGRESSIVE',
    routes: {
      progressiveUnits1e4: progressive,
      weekendUnits1e4: weekend,
      strictWeekendUnits1e4: strictWeekend,
      inclusiveWeekendUnits1e4: inclusiveWeekend,
    },
    progressiveDates,
    qualifyingWeekendDatesInclusive: inclusive,
    qualifyingWeekendDatesStrict: strict,
    tenthWeekendInclusive: inclusive[9] ?? null,
    tenthWeekendStrict: strict[9] ?? null,
    expectedUnits1e4: total - baseline,
    strictExpectedUnits1e4: strictTotal - baseline,
    inclusiveExpectedUnits1e4:
      (nurses ? total : Math.max(progressive, inclusiveWeekend)) - baseline,
    reviewFlags: [
      ...(!nurses && thresholdConflict
        ? ['EXACT_FOUR_HOUR_WEEKENDS_CHANGE_CUMULATIVE_OUTCOME']
        : []),
      ...(!nurses &&
      weekendContext.some((s) =>
        (s.ordinaryDateMinutes ?? [{ date: s.date, minutes: s.ordinaryMinutes }]).some(
          (d) =>
            d.minutes > 0 &&
            ((dayOfWeek(d.date) === 6 &&
              (addDays(d.date, 1) === endExclusive || addDays(d.date, 1) === start)) ||
              (dayOfWeek(d.date) === 0 && (d.date === start || d.date === endExclusive))),
        ),
      )
        ? ['SATURDAY_OWNS_BOUNDARY_WEEKEND']
        : []),
      ...(nursesEntitlement?.reviewReasons ?? []),
    ],
    regularityEvidence: nurses ? 'PUBLISHED_ASSIGNED_ROSTER_BOUNDED_SCREEN' : null,
    nursesEntitlement,
    closedBaseline: {
      through: closedEnd,
      known: baselineKnown,
      units1e4: closed?.units1e4 ?? (baselineKnown ? 0 : null),
      evidenceRefs: closed?.evidenceRefs ?? [],
    },
    providerDecision: !nurses && routeConflict ? 'PENDING' : null,
    nursesProRataInterpretation: nurses ? 'ADOPTED_INTERPRETATION' : null,
    nursesServiceProvisoDate: nurses ? provisoDate : null,
    nursesMonths: nurses ? nursesMonths : [],
    weekendServiceYearInterpretation: 'SATURDAY_OWNS_CALENDAR_WEEKEND',
  };
}

/** Cumulative open service-year residual; fixed scope identity allows safe supersession as history grows. */
export function planShiftworkerCorrections(input: ShiftworkerHistory): CorrectionCandidate[] {
  if (input.employment === 'CASUAL') return [];
  const out: CorrectionCandidate[] = [];
  let year = shiftworkerServiceYear(
    input.startDate,
    [input.startDate, input.closedThrough ? addDays(input.closedThrough, 1) : input.startDate]
      .sort()
      .at(-1)!,
  );
  if (input.agreement === 'NURSES')
    year = {
      start: input.startDate,
      endExclusive: shiftworkerServiceYear(input.startDate, input.asOf).endExclusive,
    };
  for (let count = 0; year.start <= input.asOf && count < 100; count++) {
    const scope: LeaveCorrectionScope = {
      type: 'PERIOD',
      calendarId: input.calendarId,
      start: [
        year.start,
        input.closedThrough ? addDays(input.closedThrough, 1) : year.start,
        input.startDate,
      ]
        .sort()
        .at(-1)!,
      end: addDays(year.endExclusive, -1),
    };
    const verdict = qualifyShiftworkerYear(input, year.start, year.endExclusive);
    const periods = input.providerPeriods
      .filter((p) => p.start <= scope.end && p.end >= scope.start)
      .map((p) => {
        if (p.start >= scope.start && p.end <= scope.end) return p;
        const allocation = p.allocationDates;
        if (
          !allocation ||
          p.additionalUnits1e4 === null ||
          allocation.reduce((sum, d) => sum + d.units1e4, 0) !== p.additionalUnits1e4
        )
          return p;
        return {
          ...p,
          start: [p.start, scope.start].sort().at(-1)!,
          end: [p.end, scope.end].sort()[0]!,
          additionalUnits1e4: allocation
            .filter((d) => d.date >= scope.start && d.date <= scope.end)
            .reduce((sum, d) => sum + d.units1e4, 0),
        };
      });
    const credits = input.ledger.filter(
      (l) =>
        l.xero_employee_id === input.employeeId &&
        l.xero_leave_type_id === input.leaveTypeId &&
        l.component === 'ADDITIONAL_SHIFTWORKER' &&
        correctionScopesOverlap(l.scope, scope),
    );
    // Actual attributed closed credits (including the 27 Aug load) supply the closed baseline,
    // never subtracted again. Credits that straddle it need evidenced reallocation.
    const coverageEnd = [input.asOf, scope.end].sort()[0]!;
    const covered = [...periods].sort((a, b) => a.start.localeCompare(b.start));
    let next = scope.start;
    let coverageComplete = true;
    for (const period of covered) {
      if (period.start !== next) coverageComplete = false;
      next = addDays(period.end, 1);
    }
    coverageComplete &&= next === addDays(coverageEnd, 1);
    const closedCredits = input.ledger.filter((l) =>
      verdict.closedBaseline.evidenceRefs.includes(l.id),
    );
    const incomplete =
      !coverageComplete ||
      !input.complete ||
      input.ordinarySplitComplete !== true ||
      !/^[0-9a-f]{64}$/.test(input.shiftDigest) ||
      input.shifts.some(
        (s) =>
          s.endMinute > 1440 &&
          !s.ordinaryDateMinutes &&
          s.ordinaryMinutes !== s.endMinute - s.startMinute,
      ) ||
      !input.leaveTypeId ||
      verdict.providerDecision !== null ||
      !periods.length ||
      periods.some(
        (p) => p.start < scope.start || p.end > scope.end || p.additionalUnits1e4 === null,
      ) ||
      credits.some(
        (l) =>
          !['PERIOD', 'COMBINED'].includes(l.scope.type) ||
          ((l.scope.type === 'PERIOD' || l.scope.type === 'COMBINED') &&
            (l.scope.start < scope.start || l.scope.end > scope.end)),
      ) ||
      credits.some((l) => l.units_1e4 === null || !Number.isSafeInteger(Number(l.units_1e4)));
    const residual =
      input.agreement === 'NURSES' && verdict.nursesEntitlement?.complete !== true
        ? { disposition: 'SURFACE_ONLY' as const, units1e4: null }
        : !verdict.closedBaseline.known
          ? {
              disposition: 'BLOCKED' as const,
              reason: 'UNATTRIBUTED_HISTORY' as const,
              units1e4: null,
            }
          : incomplete
            ? {
                disposition: 'BLOCKED' as const,
                reason: 'INCOMPLETE_SCOPE' as const,
                units1e4: null,
              }
            : calculateCorrectionResidual({
                component: 'ADDITIONAL_SHIFTWORKER',
                scope,
                closedThrough: input.closedThrough,
                unattributed: credits.some((l) => l.attribution !== 'ATTRIBUTED'),
                expectedUnits1e4: verdict.expectedUnits1e4,
                observedUnits1e4: periods.reduce((sum, p) => sum + (p.additionalUnits1e4 ?? 0), 0),
                attributedUnits1e4: credits.reduce((sum, l) => sum + Number(l.units_1e4), 0),
              });
    out.push({
      staffId: input.staffId,
      employeeId: input.employeeId,
      leaveTypeId: input.leaveTypeId,
      component: 'ADDITIONAL_SHIFTWORKER',
      scope,
      residual,
      reason:
        input.agreement === 'NURSES' && verdict.nursesEntitlement?.complete !== true
          ? nursesReviewReasonText(verdict.reviewFlags) ||
            'Nurse entitlement evidence is incomplete; review required.'
          : !verdict.closedBaseline.known
            ? 'Closed additional credit, including imported loads, is unattributed.'
            : !input.leaveTypeId
              ? 'Confirm the Additional leave (shift workers) mapping before proposing a correction.'
              : verdict.providerDecision
                ? 'Shift-worker route or pro-rata interpretation requires a provider decision.'
                : incomplete
                  ? 'Approved shift history or additional accrual allocation is incomplete.'
                  : residual.disposition === 'SURFACE_ONLY'
                    ? 'Additional shift-worker leave is over-credited; review only.'
                    : 'Additional shift-worker leave, less provider accrual and attributed credits.',
      headIds: periods.map((p) => p.headId).sort(),
      ledgerIds: credits.map((l) => l.id).sort(),
      payslipIds: [...new Set(periods.flatMap((p) => p.payslipIds))].sort(),
      payRunIds: [...new Set(periods.flatMap((p) => p.payRunIds))].sort(),
      source: {
        rule: SHIFTWORKER_RULE_VERSION,
        asOf: input.asOf,
        agreement: input.agreement,
        employment: input.employment,
        serviceYear: year,
        scope,
        verdict,
        shiftDigest: input.shiftDigest,
        shiftCount: input.shifts.length,
        ordinaryMinutes: input.shifts.reduce((sum, s) => sum + s.ordinaryMinutes, 0),
        periods,
        credits,
        closedCredits: closedCredits
          .map((l) => ({
            id: l.id,
            scope: l.scope,
            attribution: l.attribution,
            units1e4: Number(l.units_1e4),
          }))
          .sort((a, b) => a.id.localeCompare(b.id)),
      },
    });
    if (input.agreement === 'NURSES') break;
    year = shiftworkerServiceYear(input.startDate, year.endExclusive);
  }
  return out;
}
